import { useTranslation } from "react-i18next";
import { HiCheckCircle } from "react-icons/hi";

import { useHubspot } from "../../../../contexts";
import { getMappingProgress } from "./mappingUtils";
import { useVisibleRoles } from "./pipelineRoles";
import { REVENUE_TYPES } from "./HsPipelinesStep";

const SummaryRow = ({ label, children }) => (
  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
    <p className="w-56 shrink-0 text-sm font-medium text-gray-700">{label}</p>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

// Read-only view of the whole configuration, shown once sync is enabled.
// Replaces the old greyed-out interactive form.
export const HsSummary = () => {
  const { hubspotCustomizations, panoraFields, localMapping, hsFields } =
    useHubspot();
  const visibleRoles = useVisibleRoles();
  const { t } = useTranslation();

  const revenueType = REVENUE_TYPES.find(
    ({ value }) => value === hubspotCustomizations?.deal_revenue_type,
  );
  const { mapped, total } = getMappingProgress(panoraFields, localMapping);
  const notUsedCount = Object.values(localMapping).filter(
    (mapping) => mapping === "none",
  ).length;

  const describeMapping = (mapping) => {
    if (mapping === "none") return "Not used";
    if (!mapping?.field) return "—";
    const fieldLabel =
      hsFields[mapping.model]?.find((f) => f.name === mapping.field)?.label ??
      mapping.field;
    return `${mapping.model}: ${fieldLabel}`;
  };

  return (
    <div className="animation-slide-up-lil-bounce flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <HiCheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-green-500" />
        <div>
          <p className="text-sm font-semibold text-green-900">Sync is active</p>
          <p className="text-sm text-green-800">
            {t("pages.settings.hubspot.settingsMapPromptDisabled")}
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow">
        <h4 className="border-b border-gray-100 px-4 py-3 font-semibold text-black">
          Pipeline configuration
        </h4>
        <div className="divide-y divide-gray-100">
          <SummaryRow label="Deal Revenue Type">
            {revenueType?.label ?? "—"}
          </SummaryRow>
          {visibleRoles.map((role) => (
            <SummaryRow key={role.key} label={role.title}>
              <p className="font-medium">
                {hubspotCustomizations?.[role.key]?.name ?? "—"}
              </p>
              {role.stageGroups.map((group) => {
                const stages = hubspotCustomizations?.[group.key] ?? [];
                return (
                  <p key={group.key} className="text-xs text-gray-500">
                    {group.label}:{" "}
                    {stages.length > 0
                      ? stages.map((stage) => stage.name).join(", ")
                      : "—"}
                  </p>
                );
              })}
            </SummaryRow>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-white shadow">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h4 className="font-semibold text-black">Field mappings</h4>
          <span className="text-xs text-gray-500">
            {mapped} of {total} handled
            {notUsedCount > 0 ? ` · ${notUsedCount} not used` : ""}
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          {Object.entries(panoraFields).map(([category, fields]) => (
            <div key={category}>
              <p className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {category}
              </p>
              {fields.map(({ field_name, display_text }) => (
                <div
                  key={field_name}
                  className="flex flex-col gap-1 px-4 py-2 sm:flex-row sm:gap-4"
                >
                  <p className="w-56 shrink-0 text-sm text-gray-700">
                    {display_text}
                  </p>
                  <p
                    className={`text-sm ${
                      localMapping[field_name] === "none"
                        ? "text-gray-400"
                        : "text-gray-900"
                    }`}
                  >
                    {describeMapping(localMapping[field_name])}
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
